import type { ConciseBranch } from '@festina/configuration-general-types/configuration'
import type { ConfigurableServer, Optional } from '@festina-life/core'
import type { FormikProps } from '@festina-life/ui/formikForms'
import type { WizardStep } from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { isProtectedBranchCategory } from '../../uiModels/branches/types'
import { getAllowedBranchCategoriesForBaseBranchCategory } from './helpers'
import { BaseBranchSelectionStep } from './steps/BaseBranchSelectionStep'
import { BranchCreationStep } from './steps/BranchCreationStep'
import { CategorySelectionStep } from './steps/CategorySelectionStep'
import { DataIngestionQuestionStep } from './steps/DataIngestionQuestionStep'
import { DataIngestionStep } from './steps/DataIngestionStep'
import { FollowsBranchSelectionStep } from './steps/FollowsBranchSelectionStep'
import { resetFields } from './steps/helpers'
import { NamingStep } from './steps/NamingStep'
import { ServerSelectionsStep } from './steps/ServerSelectionsStep'
import { SummaryStep } from './steps/SummaryStep'
import { TypesDefinitionStep } from './steps/TypesDefinitionStep'
import { TypesQuestionStep } from './steps/TypesQuestionStep'
import type { BranchCreationInput, BranchCreationWizardStepName } from './types'

type BranchCreationWizardStep = WizardStep<
  BranchCreationWizardStepName,
  FormikProps<BranchCreationInput>
>

export const useBranchWizardSteps = (
  lockedTo: Optional<{
    baseBranch: Optional<ConciseBranch>
    server: Optional<ConfigurableServer>
  }>,
): BranchCreationWizardStep[] =>
  useMemo(() => {
    const allowedCategories = getAllowedBranchCategoriesForBaseBranchCategory(
      lockedTo?.baseBranch?.branchCategory,
    )

    const categorySelectionStep: BranchCreationWizardStep = {
      id: 'categorySelectionStep',
      render: (state) => (
        <CategorySelectionStep
          state={state}
          allowedCategories={allowedCategories}
        />
      ),
      next: () => (lockedTo?.server ? 'namingStep' : 'serverSelectionStep'),
    }

    const serverSelectionStep: BranchCreationWizardStep = {
      id: 'serverSelectionStep',
      render: () => <ServerSelectionsStep />,
      next: () => 'namingStep',
      isNextAllowed: ({ errors }) => !errors.server?.length,
      onBack: (formikProps) => resetFields(formikProps, ['server']),
    }

    const nextStepAfterFollowsBranchIsDefined = (
      currentData: FormikProps<BranchCreationInput>,
    ) => {
      switch (currentData.values.category) {
        case 'main':
          return 'typesDefinitionStep'
        case 'release':
          return 'typesQuestionStep'
        case 'feature':
        case 'none':
        default:
          return 'dataIngestionQuestion'
      }
    }

    const baseBranchSelectionStep: BranchCreationWizardStep = {
      id: 'baseBranchSelectionStep',
      render: (state) => <BaseBranchSelectionStep state={state} />,
      next: (state) =>
        isProtectedBranchCategory(state.values.category)
          ? 'followsBranchSelectionStep'
          : nextStepAfterFollowsBranchIsDefined(state),
      isNextAllowed: ({ errors }) => !errors['baseBranch']?.length,
      onBack: (formikProps) => resetFields(formikProps, ['baseBranch']),
    }

    return [
      ...(allowedCategories.length > 1 ? [categorySelectionStep] : []),
      ...(!lockedTo?.server ? [serverSelectionStep] : []),
      {
        id: 'namingStep',
        render: (state) => <NamingStep state={state} />,
        next: (currentData) => {
          if (
            !currentData.values.baseBranch &&
            currentData.values.category !== 'main'
          ) {
            return 'baseBranchSelectionStep'
          }

          return isProtectedBranchCategory(currentData.values.category)
            ? 'followsBranchSelectionStep'
            : nextStepAfterFollowsBranchIsDefined(currentData)
        },
        isNextAllowed: ({ errors }) => !errors.branchName?.length,
        onBack: (formikProps) => resetFields(formikProps, ['branchName']),
      },
      ...(!lockedTo?.baseBranch ? [baseBranchSelectionStep] : []),
      {
        id: 'followsBranchSelectionStep',
        render: (state) => <FollowsBranchSelectionStep state={state} />,
        next: nextStepAfterFollowsBranchIsDefined,
        isNextAllowed: ({ errors }) => !errors['followsBranch']?.length,
        onBack: (formikProps) => resetFields(formikProps, ['followsBranch']),
      },
      {
        id: 'dataIngestionQuestion',
        render: () => <DataIngestionQuestionStep />,
        next: ({ values }) =>
          values.shouldAddData ? 'dataIngestionStep' : 'typesQuestionStep',
        onBack: (formikProps) => resetFields(formikProps, ['shouldAddData']),
      },
      {
        id: 'dataIngestionStep',
        render: (state) => <DataIngestionStep state={state} />,
        next: ({ values }) =>
          values.category === 'main'
            ? 'typesDefinitionStep'
            : 'typesQuestionStep',
        onBack: (formikProps) => resetFields(formikProps, ['importFromFile']),
        isNextAllowed: ({ errors }) => !errors['importFromFile']?.length,
      },
      {
        id: 'typesQuestionStep',
        render: (state) => <TypesQuestionStep state={state} />,
        next: ({ values }) =>
          values.shouldDefineTypes ? 'typesDefinitionStep' : 'summaryStep',
        onBack: (formikProps) =>
          resetFields(formikProps, ['shouldDefineTypes']),
      },
      {
        id: 'typesDefinitionStep',
        render: (state) => <TypesDefinitionStep state={state} />,
        next: () => 'summaryStep',
        onBack: (formikProps) =>
          resetFields(formikProps, [
            'typesVersionName',
            'typesVersionDescription',
            'typeVersionFile',
          ]),
        isNextAllowed: ({ errors }) =>
          !errors['typesVersionName']?.length &&
          !errors['typesVersionDescription']?.length &&
          !errors['typeVersionFile']?.length,
      },
      {
        id: 'summaryStep',
        render: (state) => <SummaryStep state={state} />,
        next: () => 'completedStep',
      },
      {
        id: 'completedStep',
        render: (state) => <BranchCreationStep state={state} />,
      },
    ]
  }, [lockedTo])
